/**
 * avoidOverlays — the OBSTACLE-AVOIDANCE MIDDLEWARE (design D3).
 *
 * `overlayPlacement.js` runs this between `arrow` and `size`, and this module
 * answers one question: the chain has computed a box, and that box lands on an
 * overlay that was already there — where should it go instead? The arithmetic
 * is the one the retired `components/universal/elementInspectorLayer.js`
 * (removed in th-e692n.1) shipped for the inspector panel — the placement
 * th-dmi6q introduced — lifted here so EVERY kind with `avoids: true`
 * gets it rather than the one surface that grew it.
 *
 * ── WHAT IT TRUSTS, AND WHAT IT DOES NOT DECIDE ────────────────────────────
 *
 * `obstacles` is a FUNCTION returning rects, read at placement time. Who is in
 * that list is the registry's judgement, not this module's:
 * `obstaclesFor(self)` is already filtered to the entries that are open,
 * visible, `obstacle: true`, activated BEFORE `self`, and not `self`'s parent
 * when its kind has `avoidsParent: false` — so the nesting exemption (a citation
 * hover card inside the inspector that hosts it) and the activation order arrive
 * here as an input. This middleware avoids what it is handed and asks nothing
 * about it.
 *
 * ORDERING BY ACTIVATION is what makes avoidance terminate. A newcomer moves out
 * of the way of what is already placed; nothing already placed moves because of
 * the newcomer. The relation is a strict order rather than a mutual one, so two
 * overlays cannot ping-pong and a re-placement on scroll cannot cascade.
 *
 * ── THE PASS-THROUGH IS THE COMMON CASE ────────────────────────────────────
 *
 * No obstacles, or a computed box that touches none of them, returns `{}` — `x`,
 * `y` and the placement stay exactly as the chain computed them. That is what
 * keeps "with no obstacles the result equals floating-ui's placement alone" true
 * (the engine spec's byte-identical scenario). A zero-size obstacle rect is
 * DROPPED rather than trusted: a glance card measures nothing between mounts,
 * and a 0×0 rect collides with nothing yet would still be picked as a blocker.
 *
 * ── THE CANDIDATE ORDER ────────────────────────────────────────────────────
 *
 * The blocker is the obstacle the reference sits INSIDE, else the first the
 * chain's own box hits, else the first the opposite-side candidate hits. Then,
 * in order:
 *
 *   1. the opposite side of the REFERENCE (the chain flipped over its anchor)
 *   2. right of the blocker      3. left of the blocker
 *   4. below the blocker         5. above the blocker
 *
 * A beside-candidate (2, 3) keeps the ANCHOR's vertical position, clamped into
 * the viewport, so the panel still reads as belonging to the row it opened from;
 * the other three keep the anchor's left. Each is accepted only when all four
 * edges sit inside the viewport padding, its `maxHeight` is positive, and it
 * intersects no obstacle. The first fit wins.
 *
 * WHEN NOTHING FITS the middleware returns `{}` and the computed placement
 * stands: the bands decide who paints on top. An overlapping overlay is
 * recoverable; one pushed off-screen is not.
 *
 * ── A KIND WITH A READABLE MINIMUM IS AVOIDED AT THE ROOM IT IS GIVEN ──────
 *
 * The Peek declares `readableHeight` (founder ruling 26), and the engine decides
 * its side before the chain from the anchor and the viewport (`readableSide`).
 * Avoidance keeps that decision rather than undoing it:
 *
 *   1. Its box is tested at the FULL room on its side, never at the height it
 *      measured. A body that loads after the first frame grows into that room,
 *      so an obstacle anywhere in it is already in the way: tested at its
 *      momentary height, the box would pass through, grow into the obstacle and
 *      be moved on a later pass. Nothing in that room is the pass-through, and
 *      the engine caps the box at that same room.
 *   2. Otherwise each side of the anchor keeps the room that is CLEAR: cut a
 *      `gap` short of the nearest obstacle in the box's column (the search bar
 *      across the top of the map ends the room above a low marker), and none at
 *      all when an obstacle crosses the anchor's own edge. `yieldsToOpposite`,
 *      the rule that decided the side, picks between the two clear rooms, and
 *      the box stands at the chain's x on the side it picks, capped at that
 *      room. A Peek above a low marker stays above it, under the search bar,
 *      rather than returning to the room below that it cannot be read in.
 *   3. Only when neither side of the anchor has any clear room does it fall to
 *      the blocker candidates, as every other kind does.
 *
 * None of the three reads the measured height, so a pass over the box an answer
 * produced lands where that box stands: no cap alternates and no side flips as
 * the body loads.
 *
 * ── TWO DELIBERATE DEPARTURES FROM THE RETIRED `inspectorPanelPlacement` ───
 *
 * 1. ONE SIDE VOCABULARY. The inspector reported `'above' | 'below' | 'right' |
 *    'left'`; this reports floating-ui's `'top' | 'bottom' | 'right' | 'left'`,
 *    because `placeOverlay` hands the caller `avoided.side ?? sideOf(placement)`
 *    and those two must mean the same thing. A rename, not an arithmetic change.
 * 2. ONE ACCEPTANCE TEST FOR EVERY CANDIDATE. The inspector accepted its first
 *    two candidates on "the panel FITS this side" and the rest on "all four
 *    edges are on-screen". Here every candidate takes the on-screen test, which
 *    is what the spec requires and what the middleware position makes correct:
 *    "does the panel fit this side" is the question `flip` has already answered
 *    upstream, and asking it again would reject a side the chain chose and send
 *    a panel that would have scrolled inside its own `maxHeight` somewhere worse.
 *
 * @see openspec/changes/overlay-primitive/design.md — D3
 * @see openspec/changes/overlay-primitive/specs/overlay-primitive/spec.md —
 *   "A newly activated overlay avoids every earlier obstacle…"
 */

// The engine's viewport reader, so the budget, the placement and this module can
// never disagree about how tall the screen is, and its readable rule, so the side
// avoidance keeps is chosen exactly as the side the chain was given. The import
// cycle with `overlayPlacement.js` (it imports `avoidOverlays`, this imports
// `viewportRect` and `yieldsToOpposite`) is inert: all three are hoisted function
// declarations and neither module calls into the other while it is evaluating.
import { viewportRect, yieldsToOpposite } from "./overlayPlacement";

/**
 * The obstacle rects worth avoiding.
 *
 * A surface measures zero while it is between mounts, and a zero-size rect
 * "collides" with nothing yet would still count as a blocker — so it is dropped
 * rather than trusted. `null` entries (a registered surface with no box, such as
 * an unplaced hover card) drop the same way.
 */
function usableObstacles(rects) {
  if (!Array.isArray(rects)) return [];
  return rects.filter((rect) => rect && rect.width > 0 && rect.height > 0);
}

function overlaps(a, b) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** The height a box actually occupies: its own, capped by the room it is given. */
function heightIn(room, boxHeight) {
  return boxHeight > 0 ? Math.min(boxHeight, room) : room;
}

/**
 * The answer for a kind with a readable minimum (see the header): `{}` when
 * nothing stands in the full room on the chain's side, the box at the clear room
 * of the side `yieldsToOpposite` picks, or `null` when neither side of the anchor
 * has any clear room left.
 */
function readablePlacement({
  state,
  blockers,
  anchor,
  viewport,
  padding,
  gap,
  panelWidth,
  panelHeight,
  readableHeight,
}) {
  const column = { left: state.x, right: state.x + panelWidth };
  // The full room on one side of the anchor, as the band of the viewport it spans.
  const bandOf = (side) =>
    side === "top"
      ? { top: padding, bottom: anchor.top - gap }
      : { top: anchor.bottom + gap, bottom: viewport.height - padding };
  const inBand = (side) => {
    const band = bandOf(side);
    if (!(band.bottom > band.top)) return [];
    return blockers.filter((rect) => overlaps({ ...column, ...band }, rect));
  };
  const clearRoom = (side) => {
    const band = bandOf(side);
    let room = band.bottom - band.top;
    for (const rect of inBand(side)) {
      // An obstacle across the anchor's own edge leaves this side no room at all.
      if (side === "top" ? rect.bottom > band.bottom : rect.top < band.top) return 0;
      const clear = side === "top" ? band.bottom - rect.bottom - gap : rect.top - gap - band.top;
      room = Math.min(room, clear);
    }
    return Math.max(0, room);
  };

  const own = String(state.placement).split("-")[0];
  if (inBand(own).length === 0) return {};

  const other = own === "top" ? "bottom" : "top";
  const ownRoom = clearRoom(own);
  const otherRoom = clearRoom(other);
  const side = yieldsToOpposite(ownRoom, otherRoom, readableHeight) ? other : own;
  const room = side === own ? ownRoom : otherRoom;
  if (!(room > 0)) return null;

  return {
    x: state.x,
    y: side === "top" ? anchor.top - gap - heightIn(room, panelHeight) : anchor.bottom + gap,
    data: { side, maxHeight: room },
  };
}

/**
 * @param {object} config
 * @param {() => Array<DOMRect|null>} [config.obstacles] - the rects this overlay
 *   must not land on, read as a FUNCTION at placement time rather than as a
 *   captured array: an obstacle's box moves (the map popup on `moveend`, a
 *   chrome bar on layout) and the registry re-places later-activated overlays
 *   against its CURRENT rect. Absent, or returning an empty list, means "nothing
 *   to avoid".
 * @param {number} [config.padding] - the viewport inset every accepted candidate
 *   keeps at all four edges. The kind's own `padding`, passed by the engine.
 * @param {number} [config.gap] - the clearance between the overlay and whatever
 *   it is placed against, anchor or blocker. The kind's own `gap`.
 * @param {number} [config.readableHeight] - the least height the overlay reads
 *   at, for the one kind that declares it (see the header); absent for the rest.
 * @returns {{ name: string, options: object, fn: Function }} a floating-ui
 *   middleware. It reports `side` (the chosen candidate's) and `maxHeight` (its
 *   room) through `middlewareData.avoidOverlays`, both of which `placeOverlay`
 *   already reads; a pass-through reports neither.
 */
export function avoidOverlays({ obstacles, padding = 0, gap = 0, readableHeight } = {}) {
  return {
    name: "avoidOverlays",
    options: { obstacles, padding, gap, readableHeight },
    fn(state) {
      if (typeof obstacles !== "function") return {};

      const blockers = usableObstacles(obstacles());
      if (blockers.length === 0) return {};

      const viewport = viewportRect(state.elements?.floating);
      if (!(viewport.width > 0) || !(viewport.height > 0)) return {};

      const panelWidth = state.rects.floating.width > 0 ? state.rects.floating.width : 0;
      const panelHeight = state.rects.floating.height > 0 ? state.rects.floating.height : 0;

      /** A candidate's box, at the height it would actually occupy. */
      const boxOf = ({ left, top, maxHeight }) => ({
        left,
        right: left + panelWidth,
        top,
        bottom: top + heightIn(maxHeight, panelHeight),
      });

      // The chain's own answer, measured at its full height: a box that touches
      // nothing is the pass-through, and it is also the box the blocker search
      // asks about first.
      const computed = {
        left: state.x,
        right: state.x + panelWidth,
        top: state.y,
        bottom: state.y + panelHeight,
      };

      // The reference, in the same coordinates as everything else: `strategy:
      // 'fixed'` makes floating-ui's rects viewport-relative, which is what the
      // obstacle rects are.
      const anchor = {
        left: state.rects.reference.x,
        right: state.rects.reference.x + state.rects.reference.width,
        top: state.rects.reference.y,
        bottom: state.rects.reference.y + state.rects.reference.height,
      };

      // A kind with a readable minimum on a vertical side is avoided at the room
      // it is given (see the header); it reaches the blocker candidates below only
      // when neither side of its anchor has clear room. Every other kind passes
      // through when its measured box touches nothing.
      const chainSide = String(state.placement).split("-")[0];
      const readableKind =
        Number.isFinite(readableHeight) &&
        readableHeight > 0 &&
        (chainSide === "top" || chainSide === "bottom");
      if (readableKind) {
        const readable = readablePlacement({
          state,
          blockers,
          anchor,
          viewport,
          padding,
          gap,
          panelWidth,
          panelHeight,
          readableHeight,
        });
        if (readable) return readable;
      } else if (!blockers.some((rect) => overlaps(computed, rect))) {
        return {};
      }

      const roomBelow = viewport.height - anchor.bottom - gap - padding;
      const roomAbove = anchor.top - gap - padding;

      // Left-align with the anchor, then pull back inside the right edge. The
      // max() runs last so an overlay wider than the viewport still starts at the
      // padding rather than at a negative offset.
      const rightLimit = viewport.width - panelWidth - padding;
      const anchorLeft = Math.max(padding, Math.min(anchor.left, rightLimit));

      /** The overlay on one vertical side of the ANCHOR, clamped into the viewport. */
      const anchorCandidate = (above) => {
        const maxHeight = Math.max(0, above ? roomAbove : roomBelow);
        const top = above
          ? anchor.top - gap - heightIn(maxHeight, panelHeight)
          : anchor.bottom + gap;

        return {
          left: anchorLeft,
          top: Math.max(padding, Math.min(top, viewport.height - padding)),
          maxHeight,
          side: above ? "top" : "bottom",
        };
      };

      const clear = (candidate) => !blockers.some((rect) => overlaps(boxOf(candidate), rect));

      const onScreen = (candidate) => {
        const height = heightIn(candidate.maxHeight, panelHeight);
        if (!(candidate.maxHeight > 0) || !(height > 0)) return false;
        if (candidate.left < padding) return false;
        if (candidate.left + panelWidth > viewport.width - padding) return false;
        if (candidate.top < padding) return false;
        return candidate.top + height <= viewport.height - padding;
      };

      // Candidate 1: the opposite side of the reference from wherever the chain
      // put the overlay. `flip` has already chosen between the two sides on room
      // alone; this is the other one, now that obstacles are in play.
      const opposite = anchorCandidate(String(state.placement).split("-")[0] !== "top");

      // The obstacle to place AGAINST: the one the reference sits inside, else
      // the one the computed box runs into, else the one the opposite side does.
      const blocking =
        blockers.find(
          (rect) =>
            anchor.left >= rect.left &&
            anchor.right <= rect.right &&
            anchor.top >= rect.top &&
            anchor.bottom <= rect.bottom,
        ) ||
        blockers.find((rect) => overlaps(computed, rect)) ||
        blockers.find((rect) => overlaps(boxOf(opposite), rect)) ||
        null;

      const candidates = [opposite];

      if (blocking) {
        // Beside the blocker, the overlay keeps the anchor's vertical position as
        // far as the viewport allows; beneath or over it, it keeps the anchor's
        // left.
        const usable = viewport.height - padding * 2;
        const besideTop = Math.max(
          padding,
          Math.min(anchor.top, viewport.height - padding - heightIn(usable, panelHeight)),
        );
        const besideMaxHeight = viewport.height - besideTop - padding;
        const aboveMaxHeight = Math.max(0, blocking.top - gap - padding);

        candidates.push(
          {
            left: blocking.right + gap,
            top: besideTop,
            maxHeight: besideMaxHeight,
            side: "right",
          },
          {
            left: blocking.left - gap - panelWidth,
            top: besideTop,
            maxHeight: besideMaxHeight,
            side: "left",
          },
          {
            left: anchorLeft,
            top: blocking.bottom + gap,
            maxHeight: viewport.height - (blocking.bottom + gap) - padding,
            side: "bottom",
          },
          {
            left: anchorLeft,
            top: blocking.top - gap - heightIn(aboveMaxHeight, panelHeight),
            maxHeight: aboveMaxHeight,
            side: "top",
          },
        );
      }

      for (const candidate of candidates) {
        if (onScreen(candidate) && clear(candidate)) {
          return {
            x: candidate.left,
            y: candidate.top,
            data: { side: candidate.side, maxHeight: candidate.maxHeight },
          };
        }
      }

      // Nothing clears the obstacles: paint where the chain says and let the
      // bands settle it.
      return {};
    },
  };
}
